import {
    FormDefinitionType,
    Patient,
    QueueEConsentPatientCompleteEmail,
    QueueEConsentPatientEmail,
} from '@/api/dtos';
import {
    AlertTitle,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { FormDefinitionContext, OnlinePatientManagementContext, PatientContext, useSnackbar } from '@ngt/opms';
import { FC, FormEvent, useCallback, useContext, useState } from 'react';

export type ConsentNotificationRecipient = 'patient' | 'investigator';

export interface ConsentNotificationActionProps {
    recipient: ConsentNotificationRecipient;
    formDefinitionId: FormDefinitionType;
}

const ConsentNotificationAction: FC<ConsentNotificationActionProps> = ({
    recipient,
    formDefinitionId,
}) => {
    const { serviceStackClient } = useContext(OnlinePatientManagementContext);
    const { data: untypedPatient } = useContext(PatientContext);
    const { data: formDefinition } = useContext(FormDefinitionContext);
    const patient = untypedPatient as Patient | undefined;
    const formName = formDefinition?.name ?? 'Consent Form';
    const { enqueueSnackbar } = useSnackbar();

    const [open, setOpen] = useState(false);
    const [queueing, setQueueing] = useState(false);

    const recipientLabel = recipient === 'patient' ? 'Patient' : 'Investigator';

    const close = useCallback(() => {
        if (!queueing) {
            setOpen(false);
        }
    }, [queueing]);

    const submit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (!patient?.id) {
            return;
        }

        setQueueing(true);

        try {
            if (recipient === 'patient') {
                await serviceStackClient.post(new QueueEConsentPatientEmail({
                    patientId: patient.id,
                    formDefinitionId,
                }));
            }
            else {
                await serviceStackClient.post(new QueueEConsentPatientCompleteEmail({
                    patientId: patient.id,
                    formDefinitionId,
                }));
            }

            setOpen(false);
            enqueueSnackbar(
                <>
                    <AlertTitle>Notification Queued</AlertTitle>
                    The {recipientLabel.toLowerCase()} notification was queued successfully.
                </>,
                { variant: 'success' }
            );
        }
        catch (error: any) {
            enqueueSnackbar(
                <>
                    <AlertTitle>Notification Not Queued</AlertTitle>
                    {error?.responseStatus?.message ??
                        error?.message ??
                        `An error occurred while attempting to queue the ${recipientLabel.toLowerCase()} notification.`}
                </>,
                { variant: 'critical' }
            );
        }
        finally {
            setQueueing(false);
        }
    }, [
        patient,
        recipient,
        serviceStackClient,
        formDefinitionId,
        enqueueSnackbar,
        recipientLabel,
    ]);

    return (
        <>
            <Button
                variant="outlined"
                color="inherit"
                onClick={() => setOpen(true)}
                disabled={!patient?.id}
            >
                Notify {recipientLabel}
            </Button>

            <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
                <Box component="form" onSubmit={submit} noValidate>
                    <DialogTitle>Notify {recipientLabel}</DialogTitle>
                    <DialogContent>
                        Are you sure you want to notify the {recipientLabel.toLowerCase()} about the {formName}?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={close} disabled={queueing}>Cancel</Button>
                        <Button type="submit" variant="contained" loading={queueing}>
                            Notify {recipientLabel}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </>
    );
};

export default ConsentNotificationAction;
